// Status constants
export const PASS = "pass";
export const SPECIAL = "special";
export const FAIL = "fail";
export const NOT_FOUND = "not-found";
export const ERROR = "error";

export const normalizePrice = (value, decimalPlaces = 4) => {
  if (value === "" || value === null || value === undefined) return "";
  const match = String(value)
    .trim()
    .replace(/,/g, "")
    .match(/^([+-]?)(\d+)(?:\.(\d*))?$/);
  if (!match) return "";

  const [, sign, integer, fraction = ""] = match;
  let digits = `${integer}${`${fraction}${"0".repeat(decimalPlaces)}`.slice(
    0,
    decimalPlaces,
  )}`;

  if (fraction[decimalPlaces] >= "5") {
    const digitArray = digits.split("");
    for (let index = digitArray.length - 1; index >= 0; index -= 1) {
      if (digitArray[index] === "9") {
        digitArray[index] = "0";
      } else {
        digitArray[index] = String(Number(digitArray[index]) + 1);
        break;
      }

      if (index === 0) digitArray.unshift("1");
    }
    digits = digitArray.join("");
  }

  const whole = decimalPlaces ? digits.slice(0, -decimalPlaces) : digits;
  const decimal = decimalPlaces ? `.${digits.slice(-decimalPlaces)}` : "";
  const isZero = /^0+$/.test(digits);

  return `${isZero ? "" : sign === "-" ? "-" : ""}${whole}${decimal}`;
};

export const sumPrices = (values, decimalPlaces = 2) => {
  const total = values.reduce((sum, value) => {
    const price = normalizePrice(value, decimalPlaces);
    if (!price) return sum;

    const [whole, fraction = ""] = price.split(".");
    return sum + BigInt(`${whole}${fraction.padEnd(decimalPlaces, "0")}`);
  }, BigInt(0));

  if (!decimalPlaces) return total.toString();

  const sign = total < 0 ? "-" : "";
  const digits = (total < 0 ? -total : total)
    .toString()
    .padStart(decimalPlaces + 1, "0");

  return `${sign}${digits.slice(0, -decimalPlaces)}.${digits.slice(
    -decimalPlaces,
  )}`;
};

// Get status of an invoice item compared to Odoo data
export const getItemStatus = (
  invoiceItem,
  odooItems,
  invoiceType,
  specialItems,
) => {
  // * summary row has multiple unit prices
  if (invoiceItem.hasMultipleUnitPrice === true) {
    return FAIL;
  }

  // * no itemNumber, but is special product
  const isSpecial = specialItems[invoiceType].some(
    (item) =>
      invoiceItem.productSpec && invoiceItem.productSpec.startsWith(item),
  );
  if (isSpecial) return SPECIAL;

  // * no itemNumber, no unitPrice -> special item; no itemNumber, but has unitPrice -> not found
  if (!invoiceItem.itemNumber) {
    const hasUnitPrice =
      invoiceItem.unitPrice && String(invoiceItem.unitPrice).trim() !== "";

    return hasUnitPrice ? NOT_FOUND : SPECIAL;
  }

  // * query Odoo fail
  if (!odooItems) return ERROR;

  // * not found in Odoo
  const normalizedItemNumber = normalizeItemNumber(invoiceItem.itemNumber);
  const poItem = odooItems[normalizedItemNumber];
  if (!poItem) return NOT_FOUND;

  // * found in Odoo, check if it matches
  const invoiceQty = parseFloat(invoiceItem.quantity) || 0;
  const invoiceUnitPrice = normalizePrice(invoiceItem.unitPrice);
  const invoiceTotalPrice = normalizePrice(invoiceItem.totalPrice, 2);
  const poQty = parseFloat(poItem.quantity) || 0;
  const poUnitPrice = normalizePrice(poItem.unitPrice);
  const poTotalPrice = normalizePrice(poItem.totalPrice, 2);

  const qtyMatch = invoiceQty === poQty;
  const unitPriceMatch = invoiceUnitPrice === poUnitPrice;
  const totalPriceMatch = invoiceTotalPrice === poTotalPrice;

  return qtyMatch && unitPriceMatch && totalPriceMatch ? PASS : FAIL;
};

export const formatNumber = (value, decimalPlaces = 4) => {
  if (value === null || value === undefined) return null;
  return normalizePrice(value, decimalPlaces);
};

// Normalize itemNumber for comparison
export const normalizeItemNumber = (itemNumber) => {
  if (!itemNumber) return "";
  return String(itemNumber).trim().toLowerCase();
};

// Generate a random key for items without itemNumber
const generateRandomKey = () =>
  `unknown-${Math.random().toString(36).substring(2, 9)}`;

// Group invoice items by item number and calculate summaries
export const groupInvoiceItemsByNumber = (invoiceItems) => {
  const groupedByItemNumber = {};

  invoiceItems.forEach((item) => {
    // preprocess item
    const processedItem = {
      ...item,
      quantity: parseFloat(item.quantity) || 0,
      unitPrice: parseFloat(item.unitPrice) || 0,
    };

    // handle missing itemNumber
    if (!processedItem.itemNumber) {
      const uniqueKey = generateRandomKey();
      groupedByItemNumber[uniqueKey] = {
        items: [processedItem],
        summary: { ...processedItem },
      };
      return;
    }

    // init group
    const itemKey = processedItem.itemNumber;
    if (!groupedByItemNumber[itemKey]) {
      groupedByItemNumber[itemKey] = {
        items: [],
        summary: {
          itemNumber: itemKey,
          quantity: 0,
          unitPrice: processedItem.unitPrice,
          totalPrice: "0.00",
          hasMultipleUnitPrice: false,
          productSpec: processedItem.productSpec,
        },
      };
    }

    const group = groupedByItemNumber[itemKey];

    // check unit price consistency
    if (
      group.items.length > 0 &&
      group.items[0].unitPrice !== processedItem.unitPrice
    ) {
      group.summary.hasMultipleUnitPrice = true;
    }

    group.items.push(processedItem);

    // update summary
    group.summary.quantity += processedItem.quantity;

    group.summary.totalPrice = sumPrices(
      [group.summary.totalPrice, processedItem.totalPrice],
      2,
    );
  });

  return groupedByItemNumber;
};
